/**
 * CustomBeginnerFlow.jsx — Parcours "Choisir moi-même" du créateur de programme débutant.
 * Split → (par séance) groupes musculaires → matériel → exercices (grande bibliothèque photo)
 * → réglages séries/reps/repos/tempo/type de série avec temps estimé en direct → récapitulatif.
 * Réutilise le même payload final que AssistedProgramBuilder (POST /api/tracking/programs-pro).
 */
import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Sparkles, Check, Loader2, Plus, X, Minus,
  Clock, Unlink, ChevronDown, ImageOff, GripVertical, Trash2, RefreshCcw, BookOpen, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useClientAuth } from '../../../context/ClientAuthContext';
import {
  SPLIT_TYPES, getSplit, recommendSplitValue, ALL_MUSCLE_GROUPS, EQUIPMENT_TYPES,
  defaultEquipmentFor, SIMPLE_SET_TYPES,
} from './splitRecommender';
import { estimateSessionMinutes, suggestAdjustment, autoShrinkSession } from './sessionTimeEstimator';
import { buildSessionForTarget, TOLERANCE_MINUTES } from './exerciseSelectionEngine';
import { GOAL_PARAMS } from './goalParams';
import { ExercisePickerModal } from './ExercisePickerModal';
import { LibraryPickerModal } from './LibraryPickerModal';
import { ExerciseMedia } from './ExerciseLibraryUI';

const LEVEL_SLUG = { 'Débutant': 'debutant', 'Intermédiaire': 'intermediaire', 'Avancé': 'avance' };
const LEVEL_RANK = { debutant: 0, intermediaire: 1, avance: 2, pro: 3 };
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const shuffle = (arr) => arr.map((v) => [Math.random(), v]).sort((a, b) => a[0] - b[0]).map(([, v]) => v);

// Après un glisser-déposer, "casse" tout groupe (superset/biset/...) devenu non-contigu —
// évite un état incohérent où un groupe affiché "×2" n'a plus ses membres côte à côte.
const normalizeGroups = (exercises) => {
  const groupCounts = {};
  exercises.forEach((e) => { if (e.group_id) groupCounts[e.group_id] = (groupCounts[e.group_id] || 0) + 1; });
  const result = [];
  let i = 0;
  while (i < exercises.length) {
    const e = exercises[i];
    if (!e.group_id) { result.push(e); i += 1; continue; }
    let j = i;
    while (j < exercises.length && exercises[j].group_id === e.group_id) j += 1;
    const runLen = j - i;
    if (runLen === groupCounts[e.group_id] && runLen >= 2) {
      for (let k = i; k < j; k++) result.push(exercises[k]);
    } else {
      for (let k = i; k < j; k++) result.push({ ...exercises[k], group_id: null, set_type: 'straight' });
    }
    i = j;
  }
  return result;
};

// Vignette cliquable (vidéo si dispo, sinon image) — ouvre la fiche détail au clic.
// Agrandie (56px, avant 40px) + légende groupe/matériel en option pour que le client
// identifie immédiatement l'exercice, sans avoir à ouvrir la fiche détail.
const ExerciseThumb = ({ exercise, onOpen, testId, size = 'md' }) => (
  <button onClick={(e) => { e.stopPropagation(); onOpen(exercise); }} data-testid={testId}
    className={`${size === 'lg' ? 'w-14 h-14' : 'w-10 h-10'} rounded-xl bg-zinc-800 overflow-hidden shrink-0 flex items-center justify-center`}>
    {exercise.image_url || exercise.video_url ? <ExerciseMedia exercise={exercise} alt={exercise.name} autoPlayVideo={false} /> : <ImageOff className="w-3.5 h-3.5 text-zinc-600" />}
  </button>
);

// Fiche détail rapide (vidéo/image en grand + texte d'exécution + conseils pro) — accessible
// en cliquant sur n'importe quelle vignette d'exercice pendant la construction du programme.
const ExerciseQuickView = ({ exercise, en, onClose }) => {
  const L = (fr, eng) => (en ? eng : fr);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="exercise-quickview-modal">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={(e) => e.stopPropagation()}
        className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto">
        <div className="relative aspect-square bg-zinc-950">
          <ExerciseMedia exercise={exercise} alt={exercise.name} autoPlayVideo />
          <button onClick={onClose} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid="close-exercise-quickview-btn"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <h4 className="text-lg font-black text-white">{exercise.name}</h4>
          {exercise.execution ? (
            <div>
              <p className="text-[10px] font-bold text-fuchsia-300 uppercase mb-1">{L('Exécution', 'Execution')}</p>
              <p className="text-sm text-zinc-300 leading-relaxed">{exercise.execution}</p>
            </div>
          ) : (
            <p className="text-xs text-zinc-500 italic">{L('Aucune explication technique disponible pour cet exercice.', 'No technical explanation available for this exercise.')}</p>
          )}
          {exercise.tips && exercise.tips.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-emerald-300 uppercase mb-1">{L('Conseils pro', 'Pro tips')}</p>
              <ul className="space-y-1">
                {exercise.tips.map((t, i) => (
                  <li key={i} className="text-sm text-zinc-300 flex items-start gap-1.5"><span className="text-emerald-400 mt-0.5">•</span>{t}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const Chip = ({ active, onClick, children, testId, tone = 'emerald' }) => (
  <button onClick={onClick} data-testid={testId}
    className={`px-3.5 py-2 rounded-full text-sm font-bold border transition-colors ${active
      ? tone === 'emerald' ? 'bg-emerald-500/20 border-emerald-400/60 text-emerald-200' : 'bg-fuchsia-500/20 border-fuchsia-400/60 text-fuchsia-200'
      : 'bg-white/[0.04] border-white/10 text-zinc-400 hover:border-white/20'}`}>
    {children}
  </button>
);

export const CustomBeginnerFlow = ({ en, wizardData, onBack, onCreated }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const clientLevelSlug = LEVEL_SLUG[wizardData.level] || 'debutant';

  const [phase, setPhase] = useState('split'); // split | build | settings | review
  const [splitValue, setSplitValue] = useState(recommendSplitValue(wizardData.daysPerWeek, wizardData.level, wizardData.goal));
  const [sessions, setSessions] = useState([]);
  const [sessionIdx, setSessionIdx] = useState(0);
  const [subPhase, setSubPhase] = useState('muscles'); // muscles | equipment | exercises
  const [pickerGroup, setPickerGroup] = useState(null);
  const [settingsPickerFor, setSettingsPickerFor] = useState(null); // sIdx pour "Ajouter un exercice" en écran réglages
  const [programName, setProgramName] = useState(`${wizardData.goal} · ${wizardData.daysPerWeek}x/${L('semaine', 'week')}`);
  const [busy, setBusy] = useState(false);
  const [autoBuilding, setAutoBuilding] = useState(false);
  const [expandedUid, setExpandedUid] = useState(null);
  const [setTypes, setSetTypes] = useState([]);
  const [detailEx, setDetailEx] = useState(null);
  const [fullLibrary, setFullLibrary] = useState(null);

  const injuryGroups = wizardData.injuryGroups || [];
  const availableMuscleGroups = ALL_MUSCLE_GROUPS.filter((g) => !injuryGroups.includes(g));

  useEffect(() => {
    authFetch('/api/tracking/programs-pro/set-types').then((r) => r.json()).then((d) => setSetTypes(d.set_types || [])).catch(() => {});
    authFetch('/api/exercises?limit=1000').then((r) => r.json()).then((d) => setFullLibrary(d.exercises || [])).catch(() => setFullLibrary([]));
  }, [authFetch]);

  const recommendedSplit = useMemo(
    () => recommendSplitValue(wizardData.daysPerWeek, wizardData.level, wizardData.goal),
    [wizardData],
  );

  const startCustom = (value) => {
    const split = getSplit(value);
    const built = split.buildSessions(wizardData.daysPerWeek).map((s) => ({
      session_label: s.label, groups: s.groups, equipment: defaultEquipmentFor(wizardData), exercises: [],
    }));
    setSplitValue(value);
    setSessions(built);
    setSessionIdx(0);
    setSubPhase('muscles');
    setPhase('build');
  };

  const currentSession = sessions[sessionIdx];

  const toggleSessionGroup = (group) => {
    setSessions((prev) => prev.map((s, i) => i !== sessionIdx ? s : {
      ...s, groups: s.groups.includes(group) ? s.groups.filter((g) => g !== group) : [...s.groups, group],
    }));
  };

  const toggleSessionEquipment = (eq) => {
    setSessions((prev) => prev.map((s, i) => i !== sessionIdx ? s : {
      ...s, equipment: s.equipment.includes(eq) ? s.equipment.filter((e) => e !== eq) : [...s.equipment, eq],
    }));
  };

  const addExercise = (sIdx, libEx) => {
    const params = GOAL_PARAMS[wizardData.goal] || { sets: 3, reps: '10-12', rest: 60 };
    const isTimeBased = libEx.type === 'cardio' || /gainage|planche/i.test(libEx.name_fr || '');
    const newEx = {
      _uid: uid(), exercise_id: libEx.id, name: libEx.name_fr, muscle_group: libEx.muscle_group,
      set_type: 'straight', group_id: null,
      sets_count: params.sets, reps_scheme: isTimeBased ? '30-45s' : params.reps, is_time_based: isTimeBased,
      rest_seconds: libEx.rest_default_sec || params.rest, tempo: libEx.tempo_default || '',
      youtube_url: '', notes: '', image_url: libEx.image_url,
      // Champs conservés uniquement pour l'affichage (vidéo + fiche détail), pas envoyés au backend.
      video_url: libEx.video_url, video_status: libEx.video_status, video_disabled: libEx.video_disabled,
      execution: libEx.execution, tips: libEx.tips,
    };
    setSessions((prev) => prev.map((s, i) => i !== sIdx ? s : { ...s, exercises: [...s.exercises, newEx] }));
  };

  const removeExercise = (sIdx, exUid) => {
    setSessions((prev) => prev.map((s, i) => {
      if (i !== sIdx) return s;
      const target = s.exercises.find((e) => e._uid === exUid);
      const groupId = target?.group_id;
      const remaining = s.exercises.filter((e) => e._uid !== exUid);
      if (!groupId) return { ...s, exercises: remaining };
      const stillInGroup = remaining.filter((e) => e.group_id === groupId).length;
      return {
        ...s,
        exercises: remaining.map((e) => (e.group_id === groupId && stillInGroup < 2) ? { ...e, group_id: null, set_type: 'straight' } : e),
      };
    }));
  };

  const autoBuildCurrentSession = (sIdx, { regenerate = false } = {}) => {
    if (!fullLibrary) return;
    setAutoBuilding(true);
    const session = sessions[sIdx];
    const pool = fullLibrary
      .filter((e) => session.groups.includes(e.muscle_group) && session.equipment.includes(e.equipment))
      .slice();
    // Priorise les exercices adaptés au niveau du client, puis mélange légèrement pour varier
    // le résultat à chaque "Régénérer" (le premier essai reste déterministe pour la reproductibilité).
    const sorted = pool.sort((a, b) => {
      const aRec = LEVEL_RANK[a.level] <= LEVEL_RANK[clientLevelSlug] ? 1 : 0;
      const bRec = LEVEL_RANK[b.level] <= LEVEL_RANK[clientLevelSlug] ? 1 : 0;
      return bRec - aRec;
    });
    const library = regenerate ? shuffle(sorted) : sorted;
    const usedElsewhere = new Set(
      sessions.filter((_, i) => i !== sIdx).flatMap((s) => s.exercises.map((e) => e.name)),
    );
    const result = buildSessionForTarget({
      groups: session.groups, equipment: session.equipment, targetMinutes,
      goal: wizardData.goal, library, usedNamesGlobal: usedElsewhere,
    });
    setSessions((prev) => prev.map((s, i) => i !== sIdx ? s : { ...s, exercises: result.exercises }));
    if (result.insufficientPool) {
      toast.error(L('Pas assez d’exercices disponibles avec ce matériel pour atteindre la durée visée — ajoutez un type de matériel ou un groupe.', 'Not enough exercises available with this equipment to reach the target duration — add an equipment type or group.'));
    }
    setAutoBuilding(false);
  };

  useEffect(() => {
    if (subPhase === 'exercises' && currentSession && currentSession.exercises.length === 0 && fullLibrary) {
      autoBuildCurrentSession(sessionIdx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subPhase, sessionIdx, fullLibrary]);

  const goNextInBuild = () => {
    if (subPhase === 'muscles') {
      if (!currentSession.groups.length) { toast.error(L('Choisissez au moins un groupe musculaire.', 'Choose at least one muscle group.')); return; }
      setSubPhase('equipment');
    } else if (subPhase === 'equipment') {
      if (!currentSession.equipment.length) { toast.error(L('Choisissez au moins un type de matériel.', 'Choose at least one equipment type.')); return; }
      setSubPhase('exercises');
    } else {
      if (!currentSession.exercises.length) { toast.error(L('Ajoutez au moins un exercice pour cette séance.', 'Add at least one exercise for this session.')); return; }
      if (sessionIdx < sessions.length - 1) { setSessionIdx(sessionIdx + 1); setSubPhase('muscles'); }
      else setPhase('settings');
    }
  };

  const goBackInBuild = () => {
    if (subPhase === 'exercises') setSubPhase('equipment');
    else if (subPhase === 'equipment') setSubPhase('muscles');
    else if (sessionIdx > 0) { setSessionIdx(sessionIdx - 1); setSubPhase('exercises'); }
    else setPhase('split');
  };

  const updateExerciseField = (sIdx, exUid, patch) => {
    setSessions((prev) => prev.map((s, i) => i !== sIdx ? s : {
      ...s, exercises: s.exercises.map((e) => e._uid === exUid ? { ...e, ...patch } : e),
    }));
  };

  const linkGroup = (sIdx, exUid, setType, minExercises) => {
    setSessions((prev) => prev.map((s, i) => {
      if (i !== sIdx) return s;
      const idx = s.exercises.findIndex((e) => e._uid === exUid);
      if (idx === -1) return s;
      const span = s.exercises.slice(idx, idx + minExercises);
      if (span.length < minExercises) {
        toast.error(L(`Ajoutez encore ${minExercises - span.length} exercice(s) après celui-ci pour former ce groupe.`, `Add ${minExercises - span.length} more exercise(s) after this one to form this group.`));
        return s;
      }
      if (span.some((e) => e.group_id && e.group_id !== span[0].group_id)) {
        toast.error(L('Un des exercices suivants appartient déjà à un autre groupe — dissociez-le d’abord.', 'One of the next exercises is already part of another group — unlink it first.'));
        return s;
      }
      const gid = uid();
      const sharedRest = span[0].rest_seconds;
      return {
        ...s, exercises: s.exercises.map((e) => span.includes(e) ? { ...e, group_id: gid, set_type: setType, rest_seconds: sharedRest } : e),
      };
    }));
  };

  const unlinkGroup = (sIdx, groupId) => {
    setSessions((prev) => prev.map((s, i) => i !== sIdx ? s : {
      ...s, exercises: s.exercises.map((e) => e.group_id === groupId ? { ...e, group_id: null, set_type: 'straight' } : e),
    }));
  };

  const reorderExercises = (sIdx, newOrder) => {
    setSessions((prev) => prev.map((s, i) => i !== sIdx ? s : { ...s, exercises: normalizeGroups(newOrder) }));
  };

  const targetMinutes = wizardData.sessionDurationMinutes;

  const createProgram = async () => {
    if (!programName.trim()) { toast.error(L('Le nom du programme est requis.', 'Program name is required.')); return; }
    setBusy(true);
    try {
      const payload = {
        name: programName.trim(), goal: wizardData.goal, weeks_target: wizardData.durationWeeks, tonnage_growth_target_pct: 5,
        sessions: sessions.map((s) => ({
          session_label: s.session_label, sessions_target: wizardData.durationWeeks,
          exercises: s.exercises.map((e) => ({
            exercise_id: e.exercise_id, name: e.name, muscle_group: e.muscle_group, set_type: e.set_type,
            group_id: e.group_id, reps_scheme: e.reps_scheme, is_time_based: e.is_time_based,
            sets_count: e.sets_count, rest_seconds: e.rest_seconds, tempo: e.tempo || '', youtube_url: '', notes: '',
          })),
        })).filter((s) => s.exercises.length > 0),
      };
      if (!payload.sessions.length) { toast.error(L('Gardez au moins un exercice.', 'Keep at least one exercise.')); setBusy(false); return; }
      const res = await authFetch('/api/tracking/programs-pro', { method: 'POST', body: JSON.stringify(payload) });
      if (!res.ok) throw new Error();
      toast.success(L('Programme créé !', 'Program created!'));
      setTimeout(onCreated, 600);
    } catch {
      toast.error(L('Échec de la création.', 'Creation failed.'));
    } finally { setBusy(false); }
  };

  // ─── Écran 1 : choix du split ────────────────────────────────────────────
  if (phase === 'split') {
    return (
      <div className="space-y-5" data-testid="custom-flow-split">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="custom-split-back-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
        </button>
        <div className="text-center space-y-1.5">
          <h3 className="text-lg font-black text-white">{L('Choisissez votre structure d’entraînement', 'Choose your training structure')}</h3>
          <p className="text-xs text-zinc-500">{L(`Basé sur ${wizardData.daysPerWeek}x/semaine, niveau ${wizardData.level}, objectif ${wizardData.goal}.`, `Based on ${wizardData.daysPerWeek}x/week, level ${wizardData.level}, goal ${wizardData.goal}.`)}</p>
        </div>
        {SPLIT_TYPES.map((s) => (
          <button key={s.value} onClick={() => startCustom(s.value)} data-testid={`split-card-${s.value}`}
            className="w-full text-left rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 hover:border-fuchsia-400/40 p-4 space-y-1 transition-colors">
            <div className="flex items-center gap-2">
              <p className="font-black text-white">{L(s.label, s.label)}</p>
              {s.value === recommendedSplit && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/40">
                  <Sparkles className="w-2.5 h-2.5" /> {L('Recommandé', 'Recommended')}
                </span>
              )}
            </div>
            <p className="text-xs text-zinc-400">{L(s.longDesc, s.longDesc)}</p>
            <p className="text-[11px] text-fuchsia-300/80 italic leading-snug pt-1" data-testid={`split-why-${s.value}`}>{L(s.whyText, s.whyText)}</p>
          </button>
        ))}
      </div>
    );
  }

  // ─── Écran 2 : boucle par séance (muscles → matériel → exercices) ───────
  if (phase === 'build') {
    const stepIdx = ['muscles', 'equipment', 'exercises'].indexOf(subPhase);
    return (
      <div className="space-y-5" data-testid="custom-flow-build">
        <button onClick={goBackInBuild} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="custom-build-back-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
        </button>
        <div className="flex items-center justify-between">
          <p className="text-xs text-zinc-500 font-bold uppercase tracking-wide">{L(`Séance ${sessionIdx + 1}/${sessions.length}`, `Session ${sessionIdx + 1}/${sessions.length}`)}</p>
          <div className="flex gap-1">{['muscles', 'equipment', 'exercises'].map((_, i) => (
            <span key={i} className={`w-6 h-1 rounded-full ${i <= stepIdx ? 'bg-fuchsia-400' : 'bg-white/10'}`} />
          ))}</div>
        </div>
        <h3 className="text-lg font-black text-white">{currentSession.session_label}</h3>

        {subPhase === 'muscles' && (
          <div className="space-y-3" data-testid="custom-build-muscles">
            <p className="text-xs text-zinc-500">{L('Quels groupes musculaires travaillez-vous cette séance ?', 'Which muscle groups will you train this session?')}</p>
            <div className="flex flex-wrap gap-2">
              {availableMuscleGroups.map((g) => (
                <Chip key={g} tone="fuchsia" active={currentSession.groups.includes(g)} onClick={() => toggleSessionGroup(g)} testId={`muscle-chip-${g}`}>{g}</Chip>
              ))}
            </div>
            {injuryGroups.length > 0 && (
              <p className="text-[11px] text-amber-300/90 flex items-start gap-1.5" data-testid="muscles-injury-note">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                {L(`Groupes exclus (blessure signalée) : ${injuryGroups.join(', ')}.`, `Excluded groups (reported injury): ${injuryGroups.join(', ')}.`)}
              </p>
            )}
          </div>
        )}

        {subPhase === 'equipment' && (
          <div className="space-y-3" data-testid="custom-build-equipment">
            <p className="text-xs text-zinc-500">{L('Quel matériel utilisez-vous pour cette séance ?', 'What equipment will you use this session?')}</p>
            <div className="flex flex-wrap gap-2">
              {EQUIPMENT_TYPES.map((eq) => (
                <Chip key={eq.key} tone="fuchsia" active={currentSession.equipment.includes(eq.key)} onClick={() => toggleSessionEquipment(eq.key)} testId={`equip-chip-${eq.key}`}>{eq.label}</Chip>
              ))}
            </div>
          </div>
        )}

        {subPhase === 'exercises' && (
          <div className="space-y-4" data-testid="custom-build-exercises">
            <div className="flex items-center justify-between gap-2 rounded-2xl bg-fuchsia-500/[0.08] border border-fuchsia-500/25 px-4 py-3">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-fuchsia-300" />
                <p className="text-sm text-white font-bold" data-testid="build-exercises-time-estimate">
                  ≈ {estimateSessionMinutes(currentSession)} min <span className="text-zinc-400 font-normal text-xs">({L('cible', 'target')} {targetMinutes ? `${targetMinutes} min ±${TOLERANCE_MINUTES}` : L('non renseignée', 'not set')})</span>
                </p>
              </div>
              <button onClick={() => autoBuildCurrentSession(sessionIdx, { regenerate: true })} disabled={autoBuilding} data-testid="regenerate-session-btn"
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-fuchsia-500/20 border border-fuchsia-500/40 text-fuchsia-200 text-xs font-bold disabled:opacity-50">
                {autoBuilding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />} {L('Régénérer', 'Regenerate')}
              </button>
            </div>
            {currentSession.groups.map((group) => {
              const groupExs = currentSession.exercises.filter((e) => e.muscle_group === group);
              return (
                <div key={group} className="rounded-2xl bg-white/[0.03] border border-white/10 p-3.5 space-y-2.5">
                  <p className="text-sm font-bold text-white">{group}</p>
                  {groupExs.map((e) => (
                    <div key={e._uid} className="flex items-center gap-3 rounded-xl bg-white/[0.04] border border-white/10 p-2.5" data-testid={`session-exercise-${e._uid}`}>
                      <ExerciseThumb exercise={e} onOpen={setDetailEx} testId={`thumb-exercise-${e._uid}`} size="lg" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-semibold truncate">{e.name}</p>
                        <p className="text-[11px] text-zinc-500">{e.muscle_group} · {e.sets_count} × {e.reps_scheme}</p>
                      </div>
                      <button onClick={() => removeExercise(sessionIdx, e._uid)} className="p-1.5 text-zinc-500 hover:text-red-400" data-testid={`remove-exercise-${e._uid}`}><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button onClick={() => setPickerGroup(group)} data-testid={`add-exercise-btn-${group}`}
                    className="w-full py-2.5 rounded-lg border border-dashed border-white/15 text-zinc-400 text-xs font-bold hover:border-fuchsia-400/50 hover:text-fuchsia-300 flex items-center justify-center gap-1">
                    <Plus className="w-3.5 h-3.5" /> {L('Ajouter un exercice', 'Add an exercise')}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <button onClick={goNextInBuild} data-testid="custom-build-next-btn"
          className="w-full py-4 rounded-full font-black text-white flex items-center justify-center gap-2 bg-gradient-to-r from-fuchsia-500 to-purple-500 shadow-lg hover:scale-[1.02] active:scale-95 transition-transform">
          {L('Continuer', 'Continue')} <ChevronRight className="w-4.5 h-4.5" />
        </button>

        {pickerGroup && (
          <ExercisePickerModal en={en} muscleGroup={pickerGroup} equipmentFilter={currentSession.equipment} clientLevel={wizardData.level}
            alreadySelectedIds={new Set(currentSession.exercises.map((e) => e.exercise_id))}
            onAdd={(libEx) => addExercise(sessionIdx, libEx)} onClose={() => setPickerGroup(null)} />
        )}
        <AnimatePresence>
          {detailEx && <ExerciseQuickView exercise={detailEx} en={en} onClose={() => setDetailEx(null)} />}
        </AnimatePresence>
      </div>
    );
  }

  // ─── Écran 3 : réglages séries/reps/repos/tempo/type + temps estimé ─────
  if (phase === 'settings') {
    return (
      <div className="space-y-5" data-testid="custom-flow-settings">
        <button onClick={() => setPhase('build')} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="custom-settings-back-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
        </button>
        <div>
          <h3 className="text-lg font-black text-white">{L('Réglez vos séries, répétitions et repos', 'Set your sets, reps and rest')}</h3>
          <p className="text-xs text-zinc-500">{L('Le temps estimé se met à jour en direct. Objectif : ', 'The estimate updates live. Target: ')}<b className="text-white">{targetMinutes ? `${targetMinutes} min` : L('non renseigné', 'not set')}</b></p>
        </div>
        {sessions.map((s, sIdx) => {
          const estMin = estimateSessionMinutes(s);
          const adj = suggestAdjustment(s, targetMinutes);
          return (
            <div key={s.session_label} className="rounded-2xl bg-white/[0.035] border border-white/10 p-4 space-y-3" data-testid={`settings-session-${sIdx}`}>
              <div className="flex items-center justify-between">
                <p className="text-fuchsia-300 font-bold text-sm">{s.session_label}</p>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${adj ? 'bg-amber-500/20 text-amber-300' : 'bg-emerald-500/20 text-emerald-300'}`} data-testid={`session-time-estimate-${sIdx}`}>
                  <Clock className="w-3 h-3" /> {L('≈', '≈')} {estMin} min
                </span>
              </div>
              {adj && (
                <div className="flex items-center justify-between gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2">
                  <p className="text-[11px] text-amber-200">
                    {adj.type === 'too_long'
                      ? L(`Environ ${adj.diffMinutes} min de trop par rapport à votre objectif.`, `About ${adj.diffMinutes} min too long vs your target.`)
                      : L(`Il reste ${adj.diffMinutes} min par rapport à votre objectif.`, `${adj.diffMinutes} min left vs your target.`)}
                  </p>
                  {adj.type === 'too_long' ? (
                    <button onClick={() => setSessions((prev) => prev.map((sess, i) => i !== sIdx ? sess : autoShrinkSession(JSON.parse(JSON.stringify(sess)), targetMinutes)))}
                      data-testid={`auto-adjust-btn-${sIdx}`} className="shrink-0 text-[11px] font-bold text-amber-300 hover:text-amber-200 underline">
                      {L('Ajuster auto', 'Auto-adjust')}
                    </button>
                  ) : (
                    <button onClick={() => setSettingsPickerFor(sIdx)} data-testid={`add-exercise-adjust-btn-${sIdx}`} className="shrink-0 text-[11px] font-bold text-amber-300 hover:text-amber-200 underline">
                      {L('Ajouter un exercice', 'Add an exercise')}
                    </button>
                  )}
                </div>
              )}
              <Reorder.Group as="div" axis="y" values={s.exercises} onReorder={(newOrder) => reorderExercises(sIdx, newOrder)} className="space-y-3">
              {s.exercises.map((e) => {
                const expanded = expandedUid === e._uid;
                const groupSize = e.group_id ? s.exercises.filter((x) => x.group_id === e.group_id).length : 0;
                return (
                  <Reorder.Item as="div" key={e._uid} value={e} className="rounded-xl bg-white/[0.04] border border-white/10 p-3 space-y-2.5" data-testid={`settings-exercise-${e._uid}`}>
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-zinc-600 cursor-grab active:cursor-grabbing shrink-0" data-testid={`drag-handle-${e._uid}`} />
                      <ExerciseThumb exercise={e} onOpen={setDetailEx} testId={`thumb-settings-${e._uid}`} size="lg" />
                      <p className="flex-1 text-sm font-semibold text-white truncate">{e.name}</p>
                      {e.group_id && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-300">{setTypes.find((t) => t.value === e.set_type)?.label || e.set_type} ×{groupSize}</span>
                      )}
                      <button onClick={() => setExpandedUid(expanded ? null : e._uid)} data-testid={`expand-exercise-${e._uid}`} className="p-1 text-zinc-500"><ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} /></button>
                      <button onClick={() => removeExercise(sIdx, e._uid)} data-testid={`delete-exercise-settings-${e._uid}`} className="p-1 text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] text-zinc-500">{L('Séries', 'Sets')}</label>
                        <div className="flex items-center gap-1 mt-0.5">
                          <button onClick={() => updateExerciseField(sIdx, e._uid, { sets_count: Math.max(1, e.sets_count - 1) })} className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-white"><Minus className="w-3 h-3" /></button>
                          <span className="w-6 text-center text-white text-sm font-bold" data-testid={`sets-count-${e._uid}`}>{e.sets_count}</span>
                          <button onClick={() => updateExerciseField(sIdx, e._uid, { sets_count: e.sets_count + 1 })} className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-white"><Plus className="w-3 h-3" /></button>
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] text-zinc-500">{L('Répétitions', 'Reps')}</label>
                        <input value={e.reps_scheme} onChange={(ev) => updateExerciseField(sIdx, e._uid, { reps_scheme: ev.target.value })}
                          className="w-full mt-0.5 bg-black/30 border border-white/10 rounded px-1.5 py-1 text-white text-xs" data-testid={`reps-input-${e._uid}`} />
                      </div>
                      <div>
                        <label className="text-[10px] text-zinc-500">{L('Repos (s)', 'Rest (s)')}</label>
                        <div className="flex items-center gap-1 mt-0.5">
                          <button onClick={() => updateExerciseField(sIdx, e._uid, { rest_seconds: Math.max(15, e.rest_seconds - 15) })} className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-white"><Minus className="w-3 h-3" /></button>
                          <span className="w-8 text-center text-white text-sm font-bold" data-testid={`rest-seconds-${e._uid}`}>{e.rest_seconds}</span>
                          <button onClick={() => updateExerciseField(sIdx, e._uid, { rest_seconds: e.rest_seconds + 15 })} className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-white"><Plus className="w-3 h-3" /></button>
                        </div>
                      </div>
                    </div>
                    {expanded && (
                      <div className="space-y-2.5 pt-2 border-t border-white/10" data-testid={`advanced-panel-${e._uid}`}>
                        <div>
                          <label className="text-[10px] text-zinc-500">{L('Tempo (excentrique-pause-concentrique-pause)', 'Tempo (eccentric-pause-concentric-pause)')}</label>
                          <input value={e.tempo} onChange={(ev) => updateExerciseField(sIdx, e._uid, { tempo: ev.target.value })} placeholder="2-0-2-0"
                            className="w-full mt-0.5 bg-black/30 border border-white/10 rounded px-2 py-1.5 text-white text-xs" data-testid={`tempo-input-${e._uid}`} />
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {SIMPLE_SET_TYPES.map((tv) => {
                            const t = setTypes.find((x) => x.value === tv);
                            if (!t) return null;
                            return (
                              <Chip key={tv} tone="fuchsia" active={e.set_type === tv} testId={`settype-${tv}-${e._uid}`}
                                onClick={() => tv === 'straight'
                                  ? (e.group_id ? unlinkGroup(sIdx, e.group_id) : updateExerciseField(sIdx, e._uid, { set_type: 'straight' }))
                                  : linkGroup(sIdx, e._uid, tv, 2)}>
                                {t.label.split(' (')[0]}
                              </Chip>
                            );
                          })}
                        </div>
                        <details className="text-xs">
                          <summary className="text-zinc-500 cursor-pointer font-bold">{L('Techniques avancées (triset, drop set, pyramidal, circuit...)', 'Advanced techniques (triset, drop set, pyramid, circuit...)')}</summary>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {setTypes.filter((t) => !SIMPLE_SET_TYPES.includes(t.value)).map((t) => (
                              <Chip key={t.value} tone="fuchsia" active={e.set_type === t.value} testId={`settype-adv-${t.value}-${e._uid}`}
                                onClick={() => t.group ? linkGroup(sIdx, e._uid, t.value, t.min_exercises || 2) : updateExerciseField(sIdx, e._uid, { set_type: t.value })}>
                                {t.label.split(' (')[0]}
                              </Chip>
                            ))}
                          </div>
                        </details>
                        {e.group_id && (
                          <button onClick={() => unlinkGroup(sIdx, e.group_id)} data-testid={`unlink-group-${e._uid}`} className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-white">
                            <Unlink className="w-3 h-3" /> {L('Dissocier ce groupe', 'Unlink this group')}
                          </button>
                        )}
                      </div>
                    )}
                  </Reorder.Item>
                );
              })}
              </Reorder.Group>
              <button onClick={() => setSettingsPickerFor(sIdx)} data-testid={`settings-add-exercise-btn-${sIdx}`}
                className="w-full py-2.5 rounded-lg border border-dashed border-white/15 text-zinc-400 text-xs font-bold hover:border-fuchsia-400/50 hover:text-fuchsia-300 flex items-center justify-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" /> {L('Ajouter un exercice', 'Add an exercise')}
              </button>
            </div>
          );
        })}
        <button onClick={() => setPhase('review')} data-testid="custom-settings-next-btn"
          className="w-full py-4 rounded-full font-black text-white flex items-center justify-center gap-2 bg-gradient-to-r from-fuchsia-500 to-purple-500 shadow-lg hover:scale-[1.02] active:scale-95 transition-transform">
          {L('Voir le récapitulatif', 'See summary')} <ChevronRight className="w-4.5 h-4.5" />
        </button>
        <AnimatePresence>
          {detailEx && <ExerciseQuickView exercise={detailEx} en={en} onClose={() => setDetailEx(null)} />}
        </AnimatePresence>
        {settingsPickerFor !== null && (
          <LibraryPickerModal en={en} onClose={() => setSettingsPickerFor(null)} onAdd={(picked) => { addExercise(settingsPickerFor, picked); setSettingsPickerFor(null); }} />
        )}
      </div>
    );
  }

  // ─── Écran 4 : récapitulatif final + création ────────────────────────────
  return (
    <div className="space-y-5" data-testid="custom-flow-review">
      <button onClick={() => setPhase('settings')} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="custom-review-back-btn">
        <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
      </button>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-400 to-purple-500 flex items-center justify-center shrink-0"><Check className="w-5 h-5 text-white" /></div>
        <div>
          <h3 className="text-lg font-black text-white">{L('Votre programme sur-mesure', 'Your custom program')}</h3>
          <p className="text-xs text-zinc-500">{L(getSplit(splitValue).label, getSplit(splitValue).label)} · {sessions.length} {L('séances/semaine', 'sessions/week')}</p>
        </div>
      </div>
      <div>
        <label className="text-xs text-zinc-500 mb-1 block">{L('Nom du programme', 'Program name')}</label>
        <input value={programName} onChange={(e) => setProgramName(e.target.value)}
          className="w-full bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm" data-testid="custom-program-name-input" />
      </div>
      {sessions.map((s, sIdx) => (
        <div key={s.session_label} className="rounded-2xl bg-white/[0.035] border border-white/10 p-4 space-y-2" data-testid={`review-session-${sIdx}`}>
          <div className="flex items-center justify-between">
            <p className="text-fuchsia-300 font-bold text-sm">{s.session_label}</p>
            <span className="text-[11px] text-zinc-500">≈ {estimateSessionMinutes(s)} min</span>
          </div>
          <Reorder.Group as="div" axis="y" values={s.exercises} onReorder={(newOrder) => reorderExercises(sIdx, newOrder)} className="space-y-1.5">
            {s.exercises.map((e) => (
              <Reorder.Item as="div" key={e._uid} value={e}
                className="flex items-center gap-2 rounded-lg bg-white/[0.03] p-1.5" data-testid={`review-exercise-${e._uid}`}>
                <GripVertical className="w-4 h-4 text-zinc-600 cursor-grab active:cursor-grabbing shrink-0" data-testid={`review-drag-handle-${e._uid}`} />
                <ExerciseThumb exercise={e} onOpen={setDetailEx} testId={`thumb-review-${e._uid}`} />
                <p className="flex-1 text-xs text-zinc-300 min-w-0">
                  <span className="text-white font-semibold">{e.name}</span>{' '}
                  <span className="text-zinc-500">— {e.sets_count} × {e.reps_scheme} · {e.rest_seconds}s{e.tempo ? ` · ${e.tempo}` : ''}</span>
                </p>
                <button onClick={() => removeExercise(sIdx, e._uid)} data-testid={`delete-exercise-review-${e._uid}`} className="p-1 text-zinc-500 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
              </Reorder.Item>
            ))}
          </Reorder.Group>
          <button onClick={() => setSettingsPickerFor(sIdx)} data-testid={`review-add-exercise-btn-${sIdx}`}
            className="w-full py-2 rounded-lg border border-dashed border-white/15 text-zinc-500 text-xs font-bold hover:border-fuchsia-400/50 hover:text-fuchsia-300 flex items-center justify-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> {L('Ajouter un exercice', 'Add an exercise')}
          </button>
        </div>
      ))}
      <button onClick={createProgram} disabled={busy} data-testid="custom-create-program-btn"
        className="w-full py-4 rounded-full font-black text-white flex items-center justify-center gap-2 transition-transform active:scale-95 hover:scale-[1.02] bg-gradient-to-r from-fuchsia-500 to-purple-500 shadow-lg disabled:opacity-50">
        {busy ? <Loader2 className="w-4.5 h-4.5 animate-spin" /> : <Sparkles className="w-4.5 h-4.5" />} {L('Créer mon programme', 'Create my program')}
      </button>
      <AnimatePresence>
        {detailEx && <ExerciseQuickView exercise={detailEx} en={en} onClose={() => setDetailEx(null)} />}
      </AnimatePresence>
      {settingsPickerFor !== null && (
        <LibraryPickerModal en={en} onClose={() => setSettingsPickerFor(null)} onAdd={(picked) => { addExercise(settingsPickerFor, picked); setSettingsPickerFor(null); }} />
      )}
    </div>
  );
};

export default CustomBeginnerFlow;
